<template>
  <content-modal
    class="rich-text-modal"
    full-screen
    :visible="visible"
    :auto-hide-off="true"
    :disable-close="disableClose"
    @modal-hidden="actionHidden"
    @modal-hide-request="actionHideRequest"
  >
    <template #title>{{ params?.title }}</template>
    <template #content>
      <rich-text-editor
        ref="childEditor"
        enable-full-screen
        is-full-screen
        is-within-expanded-modal
        :disabled="params?.disabled"
        :skeleton="params?.skeleton"
        :line-count="params?.lineCount"
        :kind="params?.kind"
        :size="params?.size"
        :placeholder="params?.placeholder"
        :initial-content="params?.initialContent"
        :content="params?.content"
        :config="params?.config"
        :is-mandatory="params?.isMandatory"
        :focus-on-init="params?.focusOnInit"
        :initial-option-type="params?.initialOptionType"
        :google-document-props="params?.googleDocumentProps"
        :auto-save-indicator-state="autoSaveIndicatorState"
        :auto-save-indicator-message="autoSaveIndicatorMessage"
        :enable-tooltip="params?.enableTooltip"
        :recommended-tooltip-english-char="params?.recommendedTooltipEnglishChar"
        :recommended-tooltip-chinese-char="params?.recommendedTooltipChineseChar"
        :options-media-list="params?.optionsMediaList"
        @is-recording="handleUpdateRecordStatus"
        @change="handleUpdateAnswer"
      />
    </template>
  </content-modal>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, provide, ref, useTemplateRef } from "vue"; // Vue 3.3+
import { AUTO_SAVE_INDICATOR_STATE } from "@modules/attempt/service/attempt.constant.ts";
import modalEventBus from "@plugins/modal/service/event-bus.ts";
import type RichTextEditorType from "@plugins/richtext/components/RichTextEditor.vue";
import type { RichTextModal } from "@plugins/richtext/types.ts";
import ContentModal from "@plugins/modal/components/ContentModal.vue";
import { useVm } from "@utils/vm.ts";

const RichTextEditor = defineAsyncComponent(
  () => import("@plugins/richtext/components/RichTextEditor.vue"),
);

const props = withDefaults(
  defineProps<{
    params?: Partial<RichTextModal>;
  }>(),
  { params: undefined },
);

provide("richTextContext", props.params?.richTextContext);

const { vmNode } = useVm();

const visible = ref(true);
const disableClose = ref(false);

const autoSaveIndicatorState = computed(() => {
  const value = props.params?.autoSaveIndicatorState;
  if (typeof value === "function") {
    return value();
  }
  return value ?? AUTO_SAVE_INDICATOR_STATE.IDLE;
});

const autoSaveIndicatorMessage = computed(() => {
  const value = props.params?.autoSaveIndicatorMessage;
  if (typeof value === "function") {
    return value();
  }
  return value ?? "";
});

const childEditor = useTemplateRef<InstanceType<typeof RichTextEditorType>>("childEditor");

interface TinyMCEEditor {
  getContent: () => string;
}

function tinymceEditor(): TinyMCEEditor | undefined {
  return (
    childEditor.value?.$refs as { editor?: { getEditor: () => TinyMCEEditor } }
  )?.editor?.getEditor();
}

function actionHidden() {
  if (props.params && typeof props.params.actionHidden === "function") {
    const content = tinymceEditor()!.getContent();
    props.params.actionHidden(content);
  }
  visible.value = false;
  modalEventBus.$emit("modal-hidden", vmNode.key);
}

type CVEvent = { ["cv:reason"]?: string } & Record<string, unknown>;

function actionHideRequest(event: CVEvent) {
  if (event["cv:reason"] !== "external-click") {
    actionHidden();
  }
}

const handleUpdateAnswer = function () {
  if (props.params && typeof props.params.handleUpdateAnswer === "function") {
    const content = tinymceEditor()!.getContent();
    props.params.handleUpdateAnswer(content);
  }
};

function handleUpdateRecordStatus(status: boolean) {
  disableClose.value = status;
}
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.bx--modal.content-modal.rich-text-modal {
  .bx--modal-container {
    background-color: $ui-01;
    height: calc(100vh - 10rem);
  }

  .bx--modal-header {
    padding-top: $spacing-04;
    // For scenarios whereby there isn't a modal title. E.g MCQ option
    min-height: 2.5rem;
  }

  .bx--modal-header__heading {
    @include carbon--type-style("body-short-01", true);
    color: $text-02;
  }

  .bx--modal-content {
    padding: 0 !important;
    // To allow word-counter to be displayed at the top, which is outside of the modal-content
    overflow: visible;
  }

  .bx--modal-close {
    right: 0.75rem;

    &::before {
      background-image: url("/src/assets/images/icons/action/24px/Collapse24.svg");
      filter: saturate(40) hue-rotate(220deg) invert(0.9);
    }

    &:focus {
      border: none;
    }
  }
  .rich-text-editor {
    display: flex;
    flex-flow: column;
    height: 100%;
    margin-bottom: 0;

    // Nested FITB
    .fill-in-box {
      padding: $spacing-05;
      background-color: $field-02;
      color: $disabled-02;
      border: 1px solid $ui-03;
      border-radius: 10px;
    }

    .mce-toolbar {
      .tox-editor-container,
      .tox-toolbar,
      .tox-toolbar-overlord {
        border-radius: 0;
      }
    }

    .mce-content-body {
      flex: 1;
      border-radius: 0;

      @include media("tablet-max") {
        max-height: calc(100vh - 7.5rem);
      }

      @include media("desktop") {
        max-height: calc(100vh - 17.5rem);
      }

      &.mce-edit-focus {
        @include media("desktop") {
          max-height: calc(100vh - 22.5rem);
        }

        @include media("tablet") {
          max-height: calc(100vh - 15rem);
        }

        @include media("mobile") {
          max-height: calc(100vh - 20.5rem);
        }
      }
    }
  }

  .message-component {
    margin: $spacing-02 $spacing-05;
  }

  .rich-text-editor-footer {
    min-height: auto;
    margin: $spacing-03 $spacing-05 $spacing-05;
    row-gap: $spacing-03;
  }

  .auto-save-indicator {
    height: auto;
    align-items: center;
  }
  .word-counter {
    position: absolute;
    top: -2.25rem;
    right: 3.55rem;
  }
}
</style>
